// Telefon-Engine: genau ein Twilio Device je Fenster (Voice JS SDK, erst bei Bedarf geladen).
// Einstellungen nach Plan-Kapitel 02 Abschnitt 5.4: feste Edge frankfurt mit Ausweich dublin, Opus zuerst,
// Signalisierung bis 30 s wiederherstellen, genauere Fehlercodes, Token wird vor Ablauf erneuert.
// In der Desktop-App liegt dieselbe Engine später im verborgenen Telefon-Fenster; die Schnittstelle bleibt.
import type { Call, Device, PreflightTest } from "@twilio/voice-sdk";
import type { PreflightErgebnis } from "../api";
import { TOKEN_WIEDERHOLUNG_MS, istMikrofonGesperrt, registrierAktion, rmsDbfs, tokenAbgelaufen } from "./logik";

type Sdk = typeof import("@twilio/voice-sdk");

let sdkVersprechen: Promise<Sdk> | null = null;

export function sdkLaden(): Promise<Sdk> {
  if (!sdkVersprechen) {
    sdkVersprechen = import("@twilio/voice-sdk").catch((e) => {
      sdkVersprechen = null;
      throw e;
    });
  }
  return sdkVersprechen;
}

export interface TokenAntwort {
  token: string;
  edge: string[];
  /** Laufzeit in Sekunden (Server: ttl); fehlt sie, gilt das Token beim nächsten Neu-Anmelden als alt */
  ttl?: number;
}

/** Ereignisse des Twilio-Geräts, wie die Oberfläche sie braucht (Status melden, neu anmelden). */
export type GeraetEreignis = {
  art: "registered" | "unregistered" | "registering" | "error" | "destroyed";
  code?: number | null;
  /** true, wenn die Engine das Abmelden selbst ausgelöst hat (Zyklus, Zerstören): kein Fehlerfall */
  absichtlich?: boolean;
};

export interface RegistrierOptionen {
  /** Auch ein „registered“-Gerät neu anmelden (nach Ruhezustand, Netzwechsel) */
  erzwingen?: boolean;
  /** Ein Gespräch läuft oder es klingelt: nicht anfassen */
  belegt?: boolean;
  /** Token auf jeden Fall erneuern (Fehlercode 20104 u. ä.) */
  tokenErneuern?: boolean;
}

export const APP_VERSION = "0.1.0";

export class TelefonEngine {
  device: Device | null = null;
  private tokenHolen: () => Promise<TokenAntwort>;
  private aufbau: Promise<Device> | null = null;
  geraeteWechsel: ((verloren: MediaDeviceInfo[]) => void) | null = null;
  /** Vom Provider gesetzt: registered/unregistered/error des Geräts */
  beiGeraetEreignis: ((e: GeraetEreignis) => void) | null = null;
  private tokenUm: number | null = null;
  private tokenTtl: number | null = null;
  private tokenVersuche = 0;
  private tokenTimer: number | null = null;
  private absichtlichBis = 0;
  private get absichtlich(): boolean {
    return Date.now() < this.absichtlichBis;
  }

  constructor(tokenHolen: () => Promise<TokenAntwort>) {
    this.tokenHolen = tokenHolen;
  }

  bereit(): Promise<Device> {
    if (this.device && this.device.state === "destroyed") this.verwerfen(); // zerstört (z. B. Token ungültig): neu bauen
    if (this.device) return Promise.resolve(this.device);
    if (!this.aufbau) {
      this.aufbau = this.aufbauen().catch((e) => {
        this.aufbau = null;
        throw e;
      });
    }
    return this.aufbau;
  }

  private async aufbauen(): Promise<Device> {
    const sdk = await sdkLaden();
    const { token, edge, ttl } = await this.tokenHolen();
    this.tokenUm = Date.now();
    this.tokenTtl = ttl ?? null;
    const geraet = new sdk.Device(token, {
      edge,
      codecPreferences: [sdk.Call.Codec.Opus, sdk.Call.Codec.PCMU],
      maxAverageBitrate: 32000,
      maxCallSignalingTimeoutMs: 30000,
      closeProtection: "Es läuft ein Gespräch. Fenster wirklich schließen?",
      enableImprovedSignalingErrorPrecision: true,
      allowIncomingWhileBusy: true, // zweiter Anruf im Gespräch: eigene Zeile in der Leiste („Beenden & annehmen“)
      appName: "foerder-crm",
      appVersion: APP_VERSION,
      tokenRefreshMs: 30000,
      logLevel: "warn",
    });
    geraet.on("tokenWillExpire", () => void this.tokenErneuern(geraet));
    // Zustandswechsel des Geräts an die Oberfläche (Status ans Desktop-Fenster, Neu-Anmeldung bei Verlust)
    geraet.on("registered", () => {
      this.tokenVersuche = 0;
      this.beiGeraetEreignis?.({ art: "registered" });
    });
    geraet.on("unregistered", () => this.beiGeraetEreignis?.({ art: "unregistered", absichtlich: this.absichtlich }));
    geraet.on("registering", () => this.beiGeraetEreignis?.({ art: "registering" }));
    geraet.on("destroyed", () => this.beiGeraetEreignis?.({ art: "destroyed", absichtlich: this.absichtlich }));
    geraet.on("error", (fehler: { code?: number }) => this.beiGeraetEreignis?.({ art: "error", code: fehler?.code ?? null }));
    // Browser-Audioverarbeitung ausdrücklich an (Plan 5, Tonqualität): Echo, Rauschen, automatische Verstärkung.
    // Ohne diese Angabe hängt es vom Browser ab; ein leises Mikrofon wird mit AGC angehoben.
    // SDK-eigene Töne beim Wählen und Auflegen aus: eigene, leisere Signale in signalton.ts (19.10.2026)
    try {
      geraet.audio?.outgoing(false);
      geraet.audio?.disconnect(false);
    } catch {
      /* ältere Attrappe ohne diese Schalter */
    }
    void geraet.audio?.setAudioConstraints({ echoCancellation: true, noiseSuppression: true, autoGainControl: true }).catch(() => undefined);
    geraet.audio?.on("deviceChange", (verloren: MediaDeviceInfo[]) => this.geraeteWechsel?.(verloren));
    this.device = geraet;
    return geraet;
  }

  /** Frisches Token holen und dem Gerät geben. Bei Fehlschlag wiederholt (5, 15, 30, 60, 120 s), solange das Gerät lebt. */
  async tokenErneuern(geraet: Device | null = this.device): Promise<boolean> {
    if (!geraet) return false;
    if (this.tokenTimer !== null) {
      window.clearTimeout(this.tokenTimer);
      this.tokenTimer = null;
    }
    try {
      const neu = await this.tokenHolen();
      if (this.device !== geraet || geraet.state === "destroyed") return false;
      geraet.updateToken(neu.token);
      this.tokenUm = Date.now();
      this.tokenTtl = neu.ttl ?? null;
      this.tokenVersuche = 0;
      return true;
    } catch {
      const wartezeit = TOKEN_WIEDERHOLUNG_MS[this.tokenVersuche];
      this.tokenVersuche += 1;
      if (wartezeit !== undefined && this.device === geraet) {
        this.tokenTimer = window.setTimeout(() => void this.tokenErneuern(geraet), wartezeit);
      }
      return false;
    }
  }

  /** Lebt das Gerät, aber ist sein Token abgelaufen oder bald so weit? */
  tokenAlt(): boolean {
    return tokenAbgelaufen(this.tokenUm, this.tokenTtl, Date.now());
  }

  /** Für eingehende Anrufe bei Twilio anmelden (das Gerät klingelt, solange die Seite offen ist). */
  async registrieren(beiEingang: (anruf: Call) => void, opt: RegistrierOptionen = {}): Promise<Device> {
    const geraet = await this.bereit();
    if (!this.eingangVerdrahtet) {
      this.eingangVerdrahtet = true;
      geraet.on("incoming", (anruf: Call) => beiEingang(anruf));
    }
    const aktion = registrierAktion(geraet.state, opt);
    if (aktion === "nichts") return geraet;
    // Abgelaufenes oder ungültiges Token: vor dem Anmelden erneuern, sonst scheitert register() mit 20104/31205
    if (opt.tokenErneuern || this.tokenAlt()) {
      const ok = await this.tokenErneuern(geraet);
      if (!ok && this.tokenAlt()) throw new Error("Telefonie-Zugang konnte nicht erneuert werden.");
    }
    if (aktion === "zyklus") {
      this.absichtlichBis = Date.now() + 5000; // das „unregistered“ dazu ist gewollt, kein Verlust
      try {
        await geraet.unregister();
      } catch {
        /* war schon abgemeldet */
      } finally {
        this.absichtlichBis = Date.now() + 1500;
      }
    }
    if (geraet.state !== "registered" && geraet.state !== "registering") await geraet.register();
    return geraet;
  }

  private eingangVerdrahtet = false;

  async verbinden(params: Record<string, string>, rtcConfiguration?: RTCConfiguration): Promise<Call> {
    const geraet = await this.bereit();
    return geraet.connect({ params, rtcConfiguration });
  }

  /** Twilio-Leitungstest (etwa 10 s): MOS-Klasse, RTT, Jitter, ob TURN nötig war. */
  async preflight(): Promise<PreflightErgebnis> {
    const sdk = await sdkLaden();
    const { token, edge } = await this.tokenHolen();
    return new Promise<PreflightErgebnis>((resolve) => {
      let fertig = false;
      const ende = (e: PreflightErgebnis) => {
        if (!fertig) {
          fertig = true;
          resolve({ ...e, ts: new Date().toISOString() });
        }
      };
      let test: PreflightTest;
      try {
        test = sdk.Device.runPreflight(token, { edge: edge[0], codecPreferences: [sdk.Call.Codec.Opus, sdk.Call.Codec.PCMU] });
      } catch (e) {
        ende({ ok: false, quality: null, mos: null, rtt: null, jitter: null, edge: edge[0] ?? null, warnings: [String((e as Error).message)] });
        return;
      }
      test.on("completed", (bericht: PreflightTest.Report) => {
        ende({
          ok: true,
          quality: bericht.callQuality ?? null,
          mos: bericht.stats?.mos.average ?? null,
          rtt: bericht.stats?.rtt.average ?? null,
          jitter: bericht.stats?.jitter.average ?? null,
          edge: bericht.selectedEdge ?? bericht.edge ?? edge[0] ?? null,
          warnings: (bericht.warnings ?? []).map((w) => w.name),
          data: {
            turn_noetig: bericht.isTurnRequired ?? null,
            netz: bericht.networkTiming ?? null,
            summen: bericht.totals ?? null,
            call_sid: bericht.callSid ?? null,
            app: APP_VERSION,
          },
        });
      });
      test.on("failed", (fehler: { code?: number; message?: string }) => {
        ende({
          ok: false,
          quality: null,
          mos: null,
          rtt: null,
          jitter: null,
          edge: edge[0] ?? null,
          warnings: [fehler?.message ?? "Leitungstest fehlgeschlagen"],
          data: { code: fehler?.code ?? null },
        });
      });
      window.setTimeout(() => {
        try {
          test.stop();
        } catch {
          /* schon beendet */
        }
        ende({ ok: false, quality: null, mos: null, rtt: null, jitter: null, edge: edge[0] ?? null, warnings: ["Zeitüberschreitung"] });
      }, 30_000);
    });
  }

  /** Gerät fallen lassen, ohne die Oberfläche zu informieren (zerstörtes Gerät, Neuaufbau folgt). */
  private verwerfen(): void {
    this.device = null;
    this.aufbau = null;
    this.eingangVerdrahtet = false;
    if (this.tokenTimer !== null) {
      window.clearTimeout(this.tokenTimer);
      this.tokenTimer = null;
    }
  }

  zerstoeren(): void {
    this.absichtlichBis = Date.now() + 1500;
    try {
      this.device?.destroy();
    } catch {
      /* egal */
    }
    this.verwerfen();
    this.tokenUm = null;
    this.tokenTtl = null;
    this.tokenVersuche = 0;
  }
}

/** Misst den RMS-Pegel (dBFS) der ersten Audiospur eines Streams. Baut sich neu, wenn die Spur wechselt. */
export class PegelMesser {
  private quelle: () => MediaStream | null | undefined;
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private knoten: MediaStreamAudioSourceNode | null = null;
  private spurId: string | null = null;
  private daten = new Float32Array(2048);

  constructor(quelle: () => MediaStream | null | undefined) {
    this.quelle = quelle;
  }

  /** dBFS der aktuellen Spur; -Infinity bei digitaler Stille, null wenn (noch) nicht messbar. */
  messen(): number | null {
    const stream = this.quelle();
    const spur = stream?.getAudioTracks()[0];
    if (!spur || spur.readyState === "ended") return null;
    if (spur.id !== this.spurId) {
      try {
        this.knoten?.disconnect();
        if (!this.ctx) this.ctx = new AudioContext();
        this.analyser = this.ctx.createAnalyser();
        this.analyser.fftSize = 2048;
        this.knoten = this.ctx.createMediaStreamSource(new MediaStream([spur]));
        this.knoten.connect(this.analyser);
        this.spurId = spur.id;
      } catch {
        return null;
      }
    }
    if (!this.analyser) return null;
    if (this.ctx?.state === "suspended") void this.ctx.resume();
    this.analyser.getFloatTimeDomainData(this.daten);
    return rmsDbfs(this.daten);
  }

  stoppen(): void {
    try {
      this.knoten?.disconnect();
      void this.ctx?.close();
    } catch {
      /* egal */
    }
    this.ctx = null;
    this.analyser = null;
    this.knoten = null;
    this.spurId = null;
  }
}

/** Freundlicher Text für Fehler beim Verbindungsaufbau im Browser. */
export function verbindungsFehlerText(e: unknown): string {
  const f = e as { name?: string; code?: number; message?: string };
  if (istMikrofonGesperrt(e)) {
    return "Mikrofon nicht freigegeben. Bitte im Browser (Schloss-Symbol) bzw. in den Systemeinstellungen erlauben.";
  }
  if (f?.name === "NotFoundError" || f?.code === 31402) return "Kein Mikrofon gefunden. Headset anschließen.";
  if (f?.code === 20101 || f?.code === 20104) return "Telefonie-Zugang abgelaufen. Seite neu laden.";
  if (f?.code === 31005 || f?.code === 53000 || f?.code === 31009) {
    return "Keine Verbindung zum Telefonie-Netz (Internet, Firewall oder VPN prüfen).";
  }
  return f?.message ? `Telefonie-Fehler${f.code ? ` ${f.code}` : ""}: ${f.message}` : "Telefonie-Fehler.";
}
